import { useCallback, useState } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { EntityType } from '@/modules/entities/types/EntityType';
import { DocumentType } from '@/types/schema';

import { useMultiDocumentUploaderContext } from '../MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '../MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { DocumentUploaderModal } from './DocumentUploaderModal';

export interface StandaloneDocumentUploaderModalProps {
  householdId: string;
  entityId?: string;
  entityType?: EntityType | undefined | null;
  uploadModalOpen: boolean;
  onModalClose?: (documentIds: string[]) => void;
  additionalDetails?: React.ReactNode;
  /** Override the modal z-index (e.g. to render above a SideDrawer). */
  zIndex?: number;
}

/**
 * StandaloneDocumentUploaderModal is a modal that allows the user to upload documents to a household.
 * It *does* handle the persistence of the uploaded files, and the context is not available to the consumer.
 */
function StandaloneDocumentUploaderModalInner({
  householdId,
  entityId,
  entityType,
  uploadModalOpen,
  onModalClose,
  additionalDetails,
  zIndex,
}: StandaloneDocumentUploaderModalProps) {
  const [loading, setLoading] = useState(false);
  const { reportError } = useReportError();
  const { uploadDocuments, clearUploadedFiles, setUploaderErrorMessage } =
    useMultiDocumentUploaderContext();

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setUploaderErrorMessage(undefined);
    try {
      const documentIds = await uploadDocuments({
        householdId,
        defaultDocumentType: DocumentType.Other,
        entityId,
        entityType,
      });

      clearUploadedFiles();

      // Always call with the array of document IDs
      onModalClose?.(documentIds);
      setLoading(false);
    } catch (error) {
      reportError('Error uploading documents', error as Error);
      setUploaderErrorMessage('Error saving documents. Please try again.');
      setLoading(false);
    }
  }, [
    clearUploadedFiles,
    entityId,
    entityType,
    householdId,
    onModalClose,
    reportError,
    setUploaderErrorMessage,
    uploadDocuments,
  ]);

  const handleModalClose = useCallback(() => {
    clearUploadedFiles();
    onModalClose?.([]);
  }, [clearUploadedFiles, onModalClose]);

  return (
    <DocumentUploaderModal
      isOpen={uploadModalOpen}
      onClose={handleModalClose}
      onSubmit={handleSubmit}
      householdId={householdId}
      additionalDetails={additionalDetails}
      loading={loading}
      zIndex={zIndex}
    />
  );
}

export function StandaloneDocumentUploaderModal(
  props: StandaloneDocumentUploaderModalProps
) {
  return (
    <MultiDocumentUploaderProvider>
      <StandaloneDocumentUploaderModalInner {...props} />
    </MultiDocumentUploaderProvider>
  );
}
